import { Controller } from "@hotwired/stimulus"

// Shows the parts of the REGDPv24 form (Registrácia daní) that apply to the
// current choices and adds/removes the rows of section VII.
//
// The server renders every block with d-none already set, so this only keeps
// the page in step with the inputs. Inputs of a hidden VAT group or letter set
// are disabled, so a reason the request type no longer allows never posts.
// All validation and the XML happen on the server.
export default class extends Controller {
  static targets = [
    "subjectInput",
    "foSection",
    "poSection",
    "requestTypeInput",
    "changeDate",
    "taxKindInput",
    "taxKindDetails",
    "personRows",
    "personRow",
    "personTitle",
    "personRemoveButton",
    "personTemplate",
    "deliveryCheckbox",
    "deliveryFields",
    "vatAttachment",
    "vatGroup",
    "vatReasonInput",
    "vatReasonDetails",
    "vatReasonLetters",
    "vatReasonDate",
    "vatReasonExempt",
    "vatReasonEx"
  ]

  static values = {
    nextIndex: { type: Number, default: 0 },
    exemptLetters: { type: Array, default: ["a", "b"] }
  }

  connect() {
    this.toggleSubject()
    this.toggleRequestType()
    this.toggleTaxKind()
    this.toggleDelivery()
    this.updatePersonButtons()
  }

  toggleSubject() {
    const subject = this.checkedValue(this.subjectInputTargets)
    this.foSectionTargets.forEach((section) => this.show(section, subject === "fo"))
    this.poSectionTargets.forEach((section) => this.show(section, subject === "po"))
  }

  toggleRequestType() {
    const request = this.checkedInput(this.requestTypeInputTargets)
    this.changeDateTargets.forEach((field) => this.show(field, request?.dataset.change === "true"))
    this.toggleVatAttachment()
  }

  toggleTaxKind() {
    const ticked = new Set(this.taxKindInputTargets.filter((input) => input.checked).map((input) => input.dataset.kind))
    this.taxKindDetailsTargets.forEach((details) => this.show(details, ticked.has(details.dataset.kind)))
    this.toggleVatAttachment()
  }

  toggleDelivery() {
    if (!this.hasDeliveryCheckboxTarget || !this.hasDeliveryFieldsTarget) return

    this.show(this.deliveryFieldsTarget, this.deliveryCheckboxTarget.checked)
  }

  // Príloha 1 follows both the DPH checkbox and the request type.
  toggleVatAttachment() {
    if (!this.hasVatAttachmentTarget) return

    const vat = this.taxKindInputTargets.find((input) => input.dataset.kind === "vat")
    const group = this.checkedInput(this.requestTypeInputTargets)?.dataset.vatGroup || ""
    this.show(this.vatAttachmentTarget, Boolean(vat?.checked) && group !== "")
    this.vatGroupTargets.forEach((fieldset) => this.show(fieldset, fieldset.dataset.group === group, true))
    this.toggleVatReasonDetails()
  }

  // The letter row, the date, "výlučne" and the EX flag appear only for the
  // reason that carries them.
  toggleVatReasonDetails() {
    const reason = this.vatReasonInputTargets.find((input) => input.checked && !input.disabled)
    const key = reason?.value
    const letters = reason?.dataset.letter === "true"
    const date = reason?.dataset.date === "true"
    const ex = reason?.dataset.ex === "true"

    this.vatReasonLettersTargets.forEach((set) => this.show(set, letters && set.dataset.reason === key, true))
    const letterSet = this.vatReasonLettersTargets.find((set) => set.dataset.reason === key)
    const letter = letters ? this.checkedValue(letterSet ? letterSet.querySelectorAll("input[type='radio']") : []) : ""

    if (this.hasVatReasonDateTarget) this.show(this.vatReasonDateTarget, date)
    if (this.hasVatReasonExemptTarget) {
      this.show(this.vatReasonExemptTarget, letters && this.exemptLettersValue.includes(letter))
    }
    if (this.hasVatReasonExTarget) this.show(this.vatReasonExTarget, ex)
    if (this.hasVatReasonDetailsTarget) {
      this.placeVatReasonDetails(reason)
      this.show(this.vatReasonDetailsTarget, letters || date || ex)
    }
  }

  // The details open right under the chosen reason instead of below all of
  // them, so on a phone the required letter/date appears where the eye is.
  // A group switch disables every input of the old fieldset, the details
  // parked in it included; the date and the two flags are re-enabled here
  // (the letter sets keep their own enabled state above).
  placeVatReasonDetails(reason) {
    reason?.closest(".form-check")?.after(this.vatReasonDetailsTarget)
    ;[...this.vatReasonDateTargets, ...this.vatReasonExemptTargets, ...this.vatReasonExTargets].forEach((field) => {
      field.querySelectorAll("input").forEach((input) => {
        input.disabled = false
      })
    })
  }
  // The blank row is cloned from the server-rendered <template>; only the
  // index inside its name/id/for attributes is rewritten. Indexes stay
  // numeric so Rails reads persons[<i>][…] as rows.
  addPerson() {
    if (!this.hasPersonTemplateTarget || !this.hasPersonRowsTarget) return

    const index = String(this.nextIndexValue)
    this.nextIndexValue += 1
    const fragment = this.personTemplateTarget.content.cloneNode(true)

    fragment.querySelectorAll("[name], [id], [for]").forEach((element) => {
      ;["name", "id", "for"].forEach((attribute) => {
        const current = element.getAttribute(attribute)
        if (current) element.setAttribute(attribute, current.replaceAll("__INDEX__", index))
      })
    })

    const row = fragment.firstElementChild
    this.personRowsTarget.appendChild(fragment)
    this.updatePersonButtons()
    row?.querySelector("input:not([type='hidden']), select, textarea")?.focus()
  }

  removePerson(event) {
    const row = event.target.closest("[data-finance-management--tax-registration-target='personRow']")
    if (row) row.remove()
    this.updatePersonButtons()
  }

  // The last row cannot be removed: a member who deleted the only prefilled
  // statutory would have no way back short of reloading the page.
  updatePersonButtons() {
    const only = this.personRowTargets.length <= 1
    this.personRemoveButtonTargets.forEach((button) => {
      button.disabled = only
    })
  }

  // The row heading follows the name as it is typed; an empty name falls back
  // to the generic heading the <template> carries.
  updatePersonTitle(event) {
    const row = event.target.closest("[data-finance-management--tax-registration-target='personRow']")
    const heading = row?.querySelector("[data-finance-management--tax-registration-target='personTitle']")
    if (!heading) return

    const name = ["title_before", "first_name", "last_name", "title_after"]
      .map((field) => row.querySelector(`[name$='[${field}]']`)?.value.trim() || "")
      .filter(Boolean)
      .join(" ")
    heading.textContent = name || this.genericPersonTitle()
  }

  genericPersonTitle() {
    if (!this.hasPersonTemplateTarget) return ""

    const heading = this.personTemplateTarget.content.querySelector(
      "[data-finance-management--tax-registration-target='personTitle']"
    )
    return heading?.textContent.trim() || ""
  }

  show(element, visible, disableWhenHidden = false) {
    element.classList.toggle("d-none", !visible)
    if (!disableWhenHidden) return

    element.querySelectorAll("input, select, textarea").forEach((input) => {
      input.disabled = !visible
    })
  }

  checkedInput(inputs) {
    return Array.from(inputs).find((input) => input.checked)
  }

  checkedValue(inputs) {
    return this.checkedInput(inputs)?.value || ""
  }
}
